import AppContext from 'App/AppContext';
import { IPDropdown, IPLabel, IPSelectableOption, IPSeparator } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import GenericModal from 'components/Dialogs/GenericModal';
import OverlayLoader from 'components/Loading/OverlayLoader';
import TextInfo from 'components/Notification/Info';
import { globalStackTokensGapMedium } from 'globalStyles';
import {
  CopilotConversation,
  CopilotConversationItem,
  CopilotManagerPromptType,
} from 'models/copilot/copilotConversation';
import { PrivacyFramework } from 'models/processingActivity';
import { AIEnabled, PrivacyFrameworkSetting } from 'models/setting';
import StringValue, { DataZoneKey, StringValueTypes } from 'models/stringValue/stringValue';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiCopilotStringValueCheckNew } from 'services/Api/copilotService';
import { apiRequest } from 'services/Auth/authConfig';
import { capitalizeFirstLetter } from 'utils/string';

interface IAddStringValueModalProps {
  isOpen: boolean;
  stringValueType: StringValueTypes;
  onDismiss: () => void;
  onCreate: (newStringValue: StringValue, matchingStringValues?: StringValue[]) => Promise<void>;
}

export const AddStringValueModal = (props: IAddStringValueModalProps) => {
  const { t } = useTranslation(['translation', 'stringValue']);
  const appContext = useContext(AppContext);
  const [stringValueToAdd, setStringValueToAdd] = useState<StringValue>(new StringValue());
  const [isLoadingAI, setIsLoadingAI] = useState<boolean>(false);
  const privacyFramework =
    (appContext.globalDataCache.settings.get(PrivacyFrameworkSetting) as PrivacyFramework) ?? PrivacyFramework.GDPR;

  const dataZoneOptions: IPSelectableOption[] = [
    { key: DataZoneKey.None, text: StringValue.getDataZoneText(t, DataZoneKey.None, privacyFramework), data: DataZoneKey.None },
    { key: DataZoneKey.TenantZone, text: StringValue.getDataZoneText(t, DataZoneKey.TenantZone, privacyFramework), data: DataZoneKey.TenantZone },
    { key: DataZoneKey.Adequate, text: StringValue.getDataZoneText(t, DataZoneKey.Adequate, privacyFramework), data: DataZoneKey.Adequate },
    { key: DataZoneKey.Other, text: StringValue.getDataZoneText(t, DataZoneKey.Other, privacyFramework), data: DataZoneKey.Other },
  ];

  const onClose = () => {
    setStringValueToAdd(new StringValue());
    props.onDismiss();
  };

  const onCreate = async (stringValue: StringValue) => {
    try {
      //apply the given type
      stringValue.stringTypeId = props.stringValueType;

      //check if AI is enabled
      if (appContext.globalDataCache.settings.get(AIEnabled) !== true) {
        props.onCreate(stringValue);

        return;
      } else {
        setIsLoadingAI(true);

        const conversation = new CopilotConversation();
        conversation.request = new CopilotConversationItem();
        conversation.request.promptType = CopilotManagerPromptType.Context;

        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const stringValuesWithSameMeaning: StringValue[] | undefined = await apiCopilotStringValueCheckNew(
          conversation,
          stringValue,
          accessToken,
        );

        if (stringValuesWithSameMeaning && stringValuesWithSameMeaning.length > 0) {
          props.onCreate(stringValue, stringValuesWithSameMeaning);
        } else {
          props.onCreate(stringValue);
        }
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoadingAI(false);
    }
  };

  //
  // Main render
  //
  if (isLoadingAI) {
    return <OverlayLoader />;
  }

  return (
    <GenericModal
      title={
        capitalizeFirstLetter(
          t('stringValue:AddStringValue.Title', {
            type: StringValue.getTypeText(props.stringValueType, true, t),
          }),
        ) as string
      }
      isOpen={props.isOpen}
      onClose={onClose}
      saveDisabled={stringValueToAdd.value.trim() === ''}
      onSave={() => onCreate(stringValueToAdd)}
    >
      <IPStack verticalFill>
        <IPSeparator />
        <IPStack verticalFill tokens={globalStackTokensGapMedium}>
          <IPStack.Item>
            <IPLabel>{StringValue.getTypeText(props.stringValueType, false, t)}</IPLabel>
            <IPTextField
              autoFocus
              required
              value={stringValueToAdd.value}
              placeholder={t('stringValue:AddStringValue.Placeholder', {
                type: StringValue.getTypeText(props.stringValueType, true, t),
              })}
              onChange={(ev, newValue) => {
                const newStringValue = stringValueToAdd.clone();
                newStringValue.value = newValue?.trimStart() ?? '';
                setStringValueToAdd(newStringValue);
              }}
              maxLength={512}
            />
          </IPStack.Item>
          {props.stringValueType === StringValueTypes.Location && (
            <IPStack.Item>
              <TextInfo message={t('stringValue:AddStringValue.DataZone.NameInfo')}>
                <IPLabel>{t('stringValue:AddStringValue.DataZone.Label')}</IPLabel>
              </TextInfo>
              <IPDropdown
                options={dataZoneOptions}
                selectedKey={stringValueToAdd.stringValueKey ?? DataZoneKey.None}
                onChange={(ev, option) => {
                  const newStringValue = stringValueToAdd.clone();
                  newStringValue.stringValueKey = option?.data as number;
                  setStringValueToAdd(newStringValue);
                }}
              />
            </IPStack.Item>
          )}
          <IPStack.Item>
            <IPLabel>{t('stringValue:AddStringValue.DescriptionLabel')}</IPLabel>
            <IPTextField
              value={stringValueToAdd.description}
              onChange={(ev, newValue) => {
                const newStringValue = stringValueToAdd.clone();
                newStringValue.description = newValue?.trimStart() ?? '';
                setStringValueToAdd(newStringValue);
              }}
              maxLength={4000}
              multiline
              styles={{ field: { minHeight: 100 } }}
            />
          </IPStack.Item>
        </IPStack>
      </IPStack>
    </GenericModal>
  );
};
